import { adminFetch } from "./_client";

// Admin client for the host-cancellation review queue (Task #6249). A host's
// cancellation is held as a pending request until an admin approves (runs the
// full owner-cancel refund path) or rejects (leaves the booking active).

export interface HostCancellationScorecard {
  lifetimeTrips: number;
  acceptedCount: number;
  declinedCount: number;
  expiredCount: number;
  acceptanceRate: number | null;
  responseRate: number | null;
  pendingNow: number;
  firstTimeHost: boolean;
  inactive: boolean;
  lastLoginAt: string | null;
  hoursSinceLastLogin: number | null;
  allStar: boolean;
}

export interface HostCancellationRequestItem {
  id: number;
  status: string;
  createdAt: string;
  reason: string | null;
  reasonId: number | null;
  details: string | null;
  reviewedAt: string | null;
  reviewedBy: number | null;
  decisionNotes: string | null;
  renterExplanation?: string | null;
  trip: {
    id: number;
    publicId: string | null;
    status: string | null;
    startDate: string | null;
    endDate: string | null;
    totalAmount: number | null;
    currency: string | null;
    car: { id: number; label: string } | null;
    renter: { id: number; name: string; email: string | null } | null;
  } | null;
  host: {
    id: number;
    name: string;
    email: string | null;
    scorecard: HostCancellationScorecard;
    pastCancellations: { count: number; reasons: { reason: string; count: number }[] };
  };
}

export interface HostCancellationRequestsResponse {
  items: HostCancellationRequestItem[];
  page: number;
  pageSize: number;
  total: number;
}

export type HostCancellationRequestStatus = "pending" | "approved" | "rejected";

export function fetchHostCancellationRequests(
  token: string,
  params: { status?: HostCancellationRequestStatus; page?: number; pageSize?: number } = {},
) {
  const sp = new URLSearchParams();
  if (params.status) sp.set("status", params.status);
  if (params.page) sp.set("page", String(params.page));
  if (params.pageSize) sp.set("pageSize", String(params.pageSize));
  const qs = sp.toString();
  return adminFetch<HostCancellationRequestsResponse>(`/admin/host-cancellation-requests${qs ? `?${qs}` : ""}`, token);
}

export function approveHostCancellationRequest(token: string, id: number, decisionNotes?: string, renterExplanation?: string | null) {
  return adminFetch<{ ok: true; decision: "approved" }>(`/admin/host-cancellation-requests/${id}/approve`, token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ decisionNotes: decisionNotes ?? "", renterExplanation }),
  });
}

export function rejectHostCancellationRequest(token: string, id: number, decisionNotes?: string) {
  return adminFetch<{ ok: true; decision: "rejected" }>(`/admin/host-cancellation-requests/${id}/reject`, token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ decisionNotes: decisionNotes ?? "" }),
  });
}

// Task #6441 — one-off branded email from an admin to the host behind a
// cancellation request (any status). Replies route to the support inbox.
export function contactHostCancellationRequestHost(
  token: string,
  id: number,
  payload: { message: string; subject?: string },
) {
  return adminFetch<{ ok: true }>(`/admin/host-cancellation-requests/${id}/contact-host`, token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message: payload.message, subject: payload.subject ?? "" }),
  });
}
